import { onUnmounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { ApiError } from '@/models/errors/ApiError';
import type { ExportReportResult } from '@/services/reportsService';

const DEFAULT_QUEUED_MESSAGE = 'Your export is in progress. You’ll get an email with a download link shortly.';
const EXPORT_QUEUED_COOLDOWN_MS = 60_000;

interface Options {
    /** Modal heading shown when the backend queues the export instead of streaming it. */
    queuedTitle: string;
    /** Toast detail shown after a direct (synchronous) download. */
    successDetail: string;
    /** Toast detail shown when the request fails without a server-supplied message. */
    errorDetail: string;
}

// eslint-disable-next-line import/prefer-default-export
export function useQueuedExport(options: Options) {
    const toast = useToast();

    const exporting = ref(false);
    const cooldownActive = ref(false);
    const queuedModalVisible = ref(false);
    const queuedTitle = ref(options.queuedTitle);
    const queuedMessage = ref(DEFAULT_QUEUED_MESSAGE);

    let cooldownTimer: ReturnType<typeof setTimeout> | null = null;

    function startCooldown() {
        if (cooldownTimer !== null) {
            clearTimeout(cooldownTimer);
        }

        cooldownActive.value = true;
        cooldownTimer = setTimeout(() => {
            cooldownActive.value = false;
            cooldownTimer = null;
        }, EXPORT_QUEUED_COOLDOWN_MS);
    }

    async function run(request: () => Promise<ExportReportResult>): Promise<void> {
        if (exporting.value || cooldownActive.value) return;

        exporting.value = true;

        try {
            const result = await request();

            if (result.mode === 'queued') {
                queuedMessage.value = result.message || DEFAULT_QUEUED_MESSAGE;
                queuedModalVisible.value = true;
                startCooldown();
            } else {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: options.successDetail,
                    life: 3000,
                });
            }
        } catch (error) {
            const apiError = ApiError.from(error);
            const data = apiError?.data as { message?: string } | undefined;
            let detail = options.errorDetail;
            if (apiError?.status === 429) {
                detail = 'Too many export requests. Please wait and try again.';
            } else if (typeof data?.message === 'string') {
                detail = data.message;
            }
            toast.add({
                severity: 'error',
                summary: 'Export failed',
                detail,
                life: 4000,
            });
        } finally {
            exporting.value = false;
        }
    }

    onUnmounted(() => {
        if (cooldownTimer !== null) {
            clearTimeout(cooldownTimer);
            cooldownTimer = null;
        }
    });

    return {
        exporting,
        cooldownActive,
        queuedModalVisible,
        queuedTitle,
        queuedMessage,
        run,
    };
}
